import { useEffect, useState } from "react";
import { ArrowRight, MapPin, Gauge } from "lucide-react";
import api from "@/lib/api";
import { useCentre } from "@/context/CentreContext";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/blocks";
import { IMAGES } from "@/data/content";

const FILTERS = ["TOUT", "STAGE 1", "STAGE 2", "STAGE 3", "LIGNE", "E85", "BANC"];

function Gains({ r }) {
  if (!(r.origin_hp && r.final_hp)) {
    return (
      <p className="flex items-center gap-2 text-sm text-neutral-400 mt-4 pt-4 border-t border-white/10">
        <Gauge size={15} className="text-hp-yellow" /> {r.modification || "Passé au banc Horsepower"}
      </p>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-white/10">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-neutral-500">Puissance</p>
        <div className="flex items-center gap-2 font-stat"><span className="text-neutral-400">{r.origin_hp}</span><ArrowRight size={13} className="text-hp-yellow" /><span className="text-hp-yellow font-black text-2xl">{r.final_hp}</span><span className="text-neutral-500 text-xs self-end mb-1">CH</span></div>
      </div>
      {r.origin_nm && r.final_nm && (
        <div>
          <p className="text-[10px] uppercase tracking-widest text-neutral-500">Couple</p>
          <div className="flex items-center gap-2 font-stat"><span className="text-neutral-400">{r.origin_nm}</span><ArrowRight size={13} className="text-hp-yellow" /><span className="text-hp-yellow font-black text-2xl">{r.final_nm}</span><span className="text-neutral-500 text-xs self-end mb-1">NM</span></div>
        </div>
      )}
    </div>
  );
}

export default function Realisations() {
  const { centres } = useCentre();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("TOUT");

  useEffect(() => {
    api.get("/realisations", { params: filter === "TOUT" ? {} : { category: filter } }).then(({ data }) => setItems(data));
  }, [filter]);

  return (
    <div data-testid="realisations-page">
      <Seo title="Nos réalisations | Reprogrammation & E85 — Horsepower Garage"
        description="Découvrez les réalisations du réseau Horsepower : supercars, sportives et citadines passées au banc de puissance dans nos centres de Béziers et Perpignan."
        canonical="https://www.horsepowergarage.fr/realisations" />
      <PageHero eyebrow="Nos réalisations" title="Ils roulent Horsepower"
        subtitle="Ferrari, Porsche, AMG, BMW M… Une sélection de véhicules passés au banc de puissance dans nos centres."
        image={IMAGES.darkCar} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="flex flex-wrap gap-2 mb-10" data-testid="realisations-filters">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f.replace(/\s/g, "-").toLowerCase()}`}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border transition-colors ${
                filter === f ? "bg-hp-yellow text-black border-hp-yellow" : "border-white/15 text-neutral-300 hover:border-hp-yellow"}`}>{f}</button>
          ))}
        </div>

        {items.length === 0 ? (
          <p className="text-neutral-500 py-20 text-center">Aucune réalisation dans cette catégorie pour le moment.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((r, i) => (
              <Reveal key={r.id} delay={(i % 3) * 0.06}>
                <div className="hp-card group overflow-hidden h-full" data-testid={`realisation-${r.id}`}>
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img src={r.image.startsWith("/photos/") ? r.image.replace(".webp", "-sm.webp") : r.image} alt={`${r.vehicle} — Horsepower Garage`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    <span className="absolute left-3 top-3 bg-hp-yellow text-black text-[10px] font-bold uppercase tracking-wider px-2 py-1">{r.category}</span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-bold uppercase tracking-tight">{r.vehicle}</h3>
                    {(r.engine || centres.find((c) => c.slug === r.centre_slug)) && (
                      <p className="flex items-center gap-1.5 text-neutral-500 text-xs mt-1">
                        {r.engine && <span>{r.engine} ·</span>}
                        {r.centre_slug && <><MapPin size={12} className="text-hp-gold" /> {centres.find((c) => c.slug === r.centre_slug)?.city}</>}
                      </p>
                    )}
                    <Gains r={r} />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </section>
      <CTABand />
    </div>
  );
}
